"use client";
import {
  isRegularInsured,
  useCurrentMember,
} from "@/components/contexts/CurrentMemberContext";
import { useComposingInput } from "@/components/hooks/use-composing-input";
import { FacilityCard } from "@/components/medical-checkups/facility/FacilityCard";
import type {
  OptionSubsidy,
  PlanSubsidy,
} from "@/components/medical-checkups/facility/types";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/Spinner";
import {
  listMedicalCheckupOptions,
  listMedicalCheckupPlans,
} from "@/server/actions/members/medical-checkups/medical-checkups";
import {
  type MedicalCheckupOption,
  type MedicalCheckupPlan,
  type MedicalCheckupSegment,
  getSegmentsForSex,
  isMatchingSegment,
  isWithinFrequencySchedule,
  isWithinTargetAge,
} from "@/server/models/medical-checkup/medical-checkups";
import { calculateFiscalYearEndAge } from "@/utils/fiscal-year";
import { pagesPath } from "@/utils/pages-path";
import { useEffect, useMemo, useRef, useState } from "react";
import { LuUserRound } from "react-icons/lu";
import {
  MdHighlightOff,
  MdOutlineMedicalInformation,
  MdSearch,
} from "react-icons/md";
import { useDebounceCallback } from "usehooks-ts";
import {
  type HealthCheckupFacilitySearchResult,
  searchHealthCheckupFacilities,
} from "./_actions/search-facilities";
import { type FacilityFilterType, FacilityFilters } from "./FacilityFilters";
import { FacilityPagination } from "./FacilityPagination";
import { NoResultsContent } from "./NoResultsContent";
import { PriceExplanationDialogContent } from "./PriceExplanationDialogContent";

// 1ページあたりの施設数
const PAGE_SIZE = 10;

export const FacilityList = ({ fiscalYear }: { fiscalYear: number }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [facilities, setFacilities] = useState<
    HealthCheckupFacilitySearchResult[]
  >([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const resultsSummaryRef = useRef<HTMLDivElement>(null);
  const [planSubsidies, setPlanSubsidies] =
    useState<Record<MedicalCheckupPlan, PlanSubsidy>>();
  const [optionSubsidies, setOptionSubsidies] =
    useState<Record<MedicalCheckupOption, OptionSubsidy>>();
  const [allOptions, setAllOptions] =
    useState<
      Record<MedicalCheckupOption, { segment: MedicalCheckupSegment }>
    >();
  const [planAndOptionLoading, setPlanAndOptionLoading] = useState(true);
  const latestSearchTermRef = useRef<string>("");
  const currentMember = useCurrentMember();
  const segments = useMemo<MedicalCheckupSegment[]>(
    () => getSegmentsForSex(currentMember.sex),
    [currentMember.sex],
  );

  // 選択されたフィルタ
  const [selectedFilters, setSelectedFilters] = useState<
    Record<FacilityFilterType, boolean>
  >({
    multiLanguage: false,
    barrierFree: false,
    femaleOnly: false,
    maleOnly: false,
    directContract: false,
    toshinkyoContract: false,
  });

  // 総ページ数を計算
  const totalPages = useMemo(() => {
    return Math.ceil(totalCount / PAGE_SIZE);
  }, [totalCount]);

  const scrollToResults = () => {
    if (resultsSummaryRef.current) {
      // Calculate dynamic header height plus some padding
      const headerHeight = headerRef.current?.offsetHeight ?? 0;
      const padding = 20; // Extra padding for visual comfort
      const headerOffset = headerHeight + padding;

      // Get the element's position
      const elementPosition =
        resultsSummaryRef.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - headerOffset;

      // Ensure we don't scroll past the top of the page
      const scrollPosition = Math.max(0, offsetPosition);

      window.scrollTo({
        top: scrollPosition,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true);
    setPlanAndOptionLoading(true);

    const planPromise = listMedicalCheckupPlans({
      fiscalYear,
    }).then((result) => {
      if (result.success) {
        const planSubsidies = result.value.reduce(
          (acc, plan) => ({
            ...acc,
            [plan.plan.code]: {
              max_amount_of_subsidy: plan.maxAmountOfSubsidy,
              business_burden_amount: plan.businessBurdenAmount,
            },
          }),
          {} as Record<MedicalCheckupPlan, PlanSubsidy>,
        );
        setPlanSubsidies(planSubsidies);
        return planSubsidies;
      }
      return null;
    });

    const optionPromise = listMedicalCheckupOptions({
      fiscalYear,
      filterBySubsidy: false,
    }).then((result) => {
      if (result.success) {
        // すべてのオプションを保持
        const allOptions = result.value.reduce(
          (acc, option) => ({
            ...acc,
            [option.option.code]: {
              segment: option.segment,
            },
          }),
          {} as Record<
            MedicalCheckupOption,
            { segment: MedicalCheckupSegment }
          >,
        );
        setAllOptions(allOptions);

        // 補助のあるオプションのみを保持
        const age = currentMember.dateOfBirth
          ? calculateFiscalYearEndAge(currentMember.dateOfBirth, fiscalYear)
          : undefined;
        const optionSubsidies = result.value
          .filter(
            (d) =>
              age == null ||
              isWithinTargetAge({
                targetAge: d.targetAge,
                age,
              }),
          )
          .filter((d) =>
            isMatchingSegment({
              segment: d.segment,
              member: {
                sex: currentMember.sex ?? null,
              },
            }),
          )
          .filter(
            (d) =>
              age == null ||
              isWithinFrequencySchedule({ frequency: d.frequency, age }),
          )
          .reduce(
            (acc, option) => ({
              ...acc,
              [option.option.code]: {
                max_amount_of_subsidy: option.maxAmountOfSubsidy,
              },
            }),
            {} as Record<MedicalCheckupOption, OptionSubsidy>,
          );
        setOptionSubsidies(optionSubsidies);
        return { allOptions, optionSubsidies };
      }
      return null;
    });

    Promise.all([planPromise, optionPromise])
      .then(([planSubsidiesResult]) => {
        if (planSubsidiesResult) {
          // searchTermを参照するとdebouncedが無効になるので、latestSearchTermRefを参照する
          const currentSearchTerm = latestSearchTermRef.current || "";

          searchHealthCheckupFacilities({
            keyword: currentSearchTerm,
            fiscalYear,
            segments,
            page: 1,
            pageSize: PAGE_SIZE,
            filters: selectedFilters,
          })
            .then((result) => {
              if (result.success) {
                setFacilities(result.value.data);
                setTotalCount(result.value.totalCount);
                setHasSearched(true);
                if (
                  currentSearchTerm ||
                  Object.values(selectedFilters).some(Boolean)
                ) {
                  // 検索結果更新後に少し遅延してスクロール
                  setTimeout(scrollToResults, 100);
                }
              }
            })
            .finally(() => setLoading(false));
        }
      })
      .finally(() => setPlanAndOptionLoading(false));
  }, [
    currentMember.dateOfBirth,
    currentMember.sex,
    fiscalYear,
    segments,
    selectedFilters,
  ]);

  const debouncedSearch = useDebounceCallback((value: string) => {
    // キーワードが変わっている場合は検索しない
    if (value !== latestSearchTermRef.current) {
      return;
    }

    setLoading(true);
    setCurrentPage(1);
    searchHealthCheckupFacilities({
      keyword: value,
      fiscalYear,
      segments,
      page: 1,
      pageSize: PAGE_SIZE,
      filters: selectedFilters,
    })
      .then((result) => {
        if (result.success) {
          setFacilities(result.value.data);
          setTotalCount(result.value.totalCount);
          setHasSearched(true);
          // 検索結果更新後に少し遅延してスクロール
          setTimeout(scrollToResults, 100);
        }
      })
      .finally(() => setLoading(false));
  }, 500);

  const handleSearch = (value: string) => {
    latestSearchTermRef.current = value;
    setHasSearched(false);
    setSearchTerm(value);
    debouncedSearch(value);
  };

  // IME入力制御用のカスタムフックを使用
  const composingInputProps = useComposingInput({
    value: searchTerm,
    setter: handleSearch,
    debounceMs: 500,
  });

  // ページ変更ハンドラー
  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages || page === currentPage) {
      return;
    }

    setCurrentPage(page);
    setLoading(true);
    setHasSearched(false);

    searchHealthCheckupFacilities({
      keyword: searchTerm,
      fiscalYear,
      segments,
      page,
      pageSize: PAGE_SIZE,
      filters: selectedFilters,
    })
      .then((result) => {
        if (result.success) {
          setFacilities(result.value.data);
          setTotalCount(result.value.totalCount);
          setHasSearched(true);
        }
      })
      .finally(() => setLoading(false));
    scrollToResults();
  };

  // フィルタ変更ハンドラー
  const handleFilterChange = (filters: Record<FacilityFilterType, boolean>) => {
    // フィルタを更新すると検索が始まるので検索状態にする
    setLoading(true);
    setCurrentPage(1);
    setHasSearched(false);

    // フィルタを更新
    setSelectedFilters(filters);
  };

  // 「お探しの施設が見つかりませんでした。」を表示する条件
  const showNoResults =
    hasSearched && facilities.length === 0 && !loading && !planAndOptionLoading;

  return (
    <>
      <div className="flex flex-col gap-6">
        <div ref={headerRef} className="bg-white pt-6 pb-3 sm:pt-3">
          <div className="relative">
            <div className="absolute top-1/2 left-2.5 flex -translate-y-1/2 items-center justify-center">
              <MdSearch className="h-5 w-5 text-monotone-5" />
            </div>
            <Input
              type="search"
              inputMode="search"
              autoComplete="off"
              name="search"
              className="h-10 px-10 text-base [&::-webkit-search-cancel-button]:hidden" // クリアボタンを常時表示したいのでネイティブのクリアボタンを非表示
              disabled={planAndOptionLoading}
              {...composingInputProps}
            />
            {composingInputProps.value && (
              <div className="absolute top-1/2 right-2.5 flex -translate-y-1/2 items-center justify-center">
                <button
                  type="button"
                  onClick={() => {
                    handleSearch("");
                    setCurrentPage(1);
                  }}
                  className="flex items-center justify-center text-monotone-5 hover:text-monotone-6 focus:outline-hidden"
                >
                  <MdHighlightOff
                    fill="currentColor"
                    className="h-5 w-5 text-monotone-6"
                    color="white"
                  />
                </button>
              </div>
            )}
          </div>

          <div className="mt-12">
            <FacilityFilters
              onChange={handleFilterChange}
              sex={currentMember.sex}
              fiscalYear={fiscalYear}
            />
          </div>
        </div>

        <div
          ref={resultsSummaryRef}
          className="flex items-center justify-between pb-4 font-bold text-monotone-9"
        >
          {facilities.length > 0 ? (
            <div className="flex w-full flex-col items-start gap-4 border-b border-monotone-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex w-full items-center justify-center sm:w-auto sm:justify-start">
                <span className="text-2xl font-semibold text-primary-6">
                  {(currentPage - 1) * PAGE_SIZE + 1}-
                  {Math.min(currentPage * PAGE_SIZE, totalCount)}
                </span>
                <span className="ml-1">件 / {totalCount}件</span>
              </div>

              {totalPages > 1 && (
                <div className="flex w-full items-center justify-center sm:ml-auto sm:w-auto sm:justify-end">
                  <FacilityPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}
            </div>
          ) : hasSearched ? (
            <div className="text-lg font-bold text-monotone-6">
              該当する施設はありません
            </div>
          ) : (
            <div className="flex w-full justify-center">
              <Spinner size="sm" />
            </div>
          )}
        </div>

        {/* 説明文とボタン */}
        {facilities.length > 0 && (
          <div className="flex flex-col items-start justify-end gap-2 md:flex-row md:items-center">
            <Dialog>
              <DialogTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-2 rounded-md border border-primary-6 px-4 py-2 text-sm font-bold text-primary-6 transition-colors hover:bg-primary-0.5"
                >
                  <MdOutlineMedicalInformation className="h-4 w-4" />
                  窓口支払額・
                  <LuUserRound className="h-4 w-4" />
                  自己負担額について
                </button>
              </DialogTrigger>
              <DialogContent
                className="mx-auto max-w-[calc(100%-3rem)] p-6 md:max-w-xl"
                aria-describedby={undefined}
              >
                <DialogHeader>
                  <DialogTitle className="text-lg font-bold">
                    窓口支払額・自己負担額について
                  </DialogTitle>
                </DialogHeader>
                <PriceExplanationDialogContent
                  isRegularInsured={isRegularInsured(currentMember)}
                />
              </DialogContent>
            </Dialog>
          </div>
        )}

        <div className="relative min-h-[calc(100vh-10rem)]">
          {facilities.length > 0 && loading && (
            <>
              <div className="absolute inset-0 bg-white/80 backdrop-blur-xs" />
              <div className="fixed inset-0 z-50 flex items-center justify-center">
                <Spinner />
              </div>
            </>
          )}
          {showNoResults ? (
            <NoResultsContent fiscalYear={fiscalYear} />
          ) : (
            <>
              <div className="space-y-4">
                {planSubsidies &&
                  facilities.map((facility) => (
                    <FacilityCard
                      key={facility.code}
                      planSubsidies={planSubsidies}
                      optionSubsidies={optionSubsidies}
                      allOptions={allOptions}
                      currentMember={{
                        sex: currentMember.sex,
                        relationshipToPrimary:
                          currentMember.relationshipToPrimary ?? null,
                      }}
                      reservationFormUrl={
                        pagesPath.members.medical_checkups
                          ._fiscalYear(fiscalYear)
                          .upsert.$url({ query: { code: facility.code } }).path
                      }
                      isRegularInsured={isRegularInsured(currentMember)}
                      {...facility}
                    />
                  ))}
              </div>

              {totalPages > 1 && (
                <div className="mt-8 flex flex-col items-center border-b border-monotone-2 sm:flex-row sm:justify-between">
                  <div className="flex w-full items-center justify-center sm:w-auto sm:justify-start">
                    <span className="text-2xl font-semibold text-primary-6">
                      {(currentPage - 1) * PAGE_SIZE + 1}-
                      {Math.min(currentPage * PAGE_SIZE, totalCount)}
                    </span>
                    <span className="ml-1">件 / {totalCount}件</span>
                  </div>
                  <div className="flex w-full items-center justify-center sm:ml-auto sm:w-auto sm:justify-end">
                    <FacilityPagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};
